import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LocationPreferences = ({ locationData, onUpdateLocation }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    primaryLocation: locationData?.primaryLocation || '',
    searchRadius: locationData?.searchRadius || 25,
    autoDetectLocation: locationData?.autoDetectLocation || false,
    favoriteVenues: locationData?.favoriteVenues || []
  });

  const radiusOptions = [
    { value: 5, label: '5 miles' },
    { value: 10, label: '10 miles' },
    { value: 25, label: '25 miles' },
    { value: 50, label: '50 miles' },
    { value: 100, label: '100 miles' },
    { value: 250, label: '250 miles' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRadiusChange = (value) => {
    setFormData(prev => ({
      ...prev,
      searchRadius: value
    }));
  };

  const handleDetectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation?.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position?.coords;
          // Mock reverse geocoding
          const mockLocation = `${latitude?.toFixed(4)}, ${longitude?.toFixed(4)}`;
          setFormData(prev => ({
            ...prev,
            primaryLocation: mockLocation,
            autoDetectLocation: true
          }));
        },
        (error) => {
          console.error('Error detecting location:', error);
          alert('Unable to detect your location. Please enter it manually.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onUpdateLocation(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update location preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      primaryLocation: locationData?.primaryLocation || '',
      searchRadius: locationData?.searchRadius || 25,
      autoDetectLocation: locationData?.autoDetectLocation || false,
      favoriteVenues: locationData?.favoriteVenues || []
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
            <Icon name="MapPin" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Location Preferences</h2>
            <p className="text-sm text-muted-foreground">Customize your event discovery settings</p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="space-y-6">
        {/* Primary Location */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-foreground">Primary Location</label>
            {isEditing && (
              <Button
                variant="ghost"
                size="sm"
                iconName="MapPin"
                onClick={handleDetectLocation}
                className="text-primary hover:text-primary"
              >
                Detect Location
              </Button>
            )}
          </div>
          <Input
            type="text"
            name="primaryLocation"
            value={formData?.primaryLocation}
            onChange={handleInputChange}
            disabled={!isEditing}
            placeholder="Enter city, state or ZIP code"
            description="This will be used to show nearby events first"
          />
        </div>

        {/* Search Radius */}
        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">
            Search Radius
          </label>
          <Select
            options={radiusOptions}
            value={formData?.searchRadius}
            onChange={handleRadiusChange}
            disabled={!isEditing}
            placeholder="Select search radius"
            description="Maximum distance to search for events from your location"
          />
        </div>

        {/* Current Location Display */}
        {formData?.primaryLocation && (
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Icon name="MapPin" size={16} className="text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">Current Location</p>
                <p className="text-sm text-muted-foreground">{formData?.primaryLocation}</p>
                <p className="text-xs text-muted-foreground">
                  Searching within {formData?.searchRadius} miles
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Favorite Venues */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3">Favorite Venues</h3>
          {formData?.favoriteVenues?.length > 0 ? (
            <div className="space-y-2">
              {formData?.favoriteVenues?.map((venue) => (
                <div
                  key={venue?.id}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="Building" size={16} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{venue?.name}</p>
                      <p className="text-xs text-muted-foreground">{venue?.location}</p>
                    </div>
                  </div>
                  {isEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      className="text-error hover:text-error"
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 bg-muted/30 rounded-lg">
              <Icon name="Building" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No favorite venues yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Mark venues as favorites while browsing events
              </p>
            </div>
          )}
        </div>

        {/* Location-based Features */}
        <div className="bg-muted/50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-foreground mb-3">Location Features</h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={14} className="text-success" />
              <span>Personalized event recommendations</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={14} className="text-success" />
              <span>Distance-based search results</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={14} className="text-success" />
              <span>Local venue suggestions</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Check" size={14} className="text-success" />
              <span>Weather-aware event notifications</span>
            </div>
          </div>
        </div>

        {isEditing && (
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              variant="default"
              onClick={handleSave}
              loading={isLoading}
              iconName="Save"
              className="flex-1"
            >
              Save Preferences
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationPreferences;